import { size } from 'lodash/fp';
import {
    EXECUTE_ARBITRARY_SQL_OPERATION,
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE_OPERATION,
    SET_DATAMEER_SNOWFLAKE_WAREHOUSE_OPERATION
} from 'app/store/__generated__/operations';
import { isProject, navigateToHome } from 'app/routes';
import { toStringFqn, splitFqn, quoteIfNeeded } from 'app/lib/utils';
import { doValidateSnowflakeAccess } from 'app/store/access/actions';
import { transformSnowflakeSearchResults } from 'app/store/snowflake/transformer';
import { doGetTableDetailsForDataBrowser } from 'app/store/tableDetails/actions.js';
import {
    selectFilteredSourcesByDatabaseAndSchema,
    selectAllSourcesByDatabaseAndSchema
} from 'app/store/assets/selectors.js';
import { doDeleteFQNQuery, doDeleteRowCountQuery } from 'app/store/snowflakeQueries/actions.js';
import {
    LIST_SNOWFLAKE_WAREHOUSES,
    LIST_SNOWFLAKE_ROLES,
    LIST_USER_SESSION_WAREHOUSES,
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    SET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    GET_SNOWFLAKE_SOURCES,
    GET_SNOWFLAKE_SCHEMAS,
    GET_SNOWFLAKE_SOURCE_COLUMNS,
    SET_SNOWFLAKE_ROLE,
    SET_USER_SESSION_WAREHOUSE,
    DELETE_SNOWFLAKE_TABLE,
    GET_SNOWFLAKE_DATABASES,
    SEARCH_SNOWFLAKE_OBJECTS,
    SEARCH_SNOWFLAKE_DESTINATIONS
} from './types.js';

export const doSearchSnowflakeDestinations = (searchString) => ({
    type: SEARCH_SNOWFLAKE_DESTINATIONS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true, version: 2 },
    params: {
        sql: `SELECT SYSTEM$NGRAM_SEARCH('${searchString}', 200)`
    }
});
export const doSearchSnowflakeObjects = (searchString) => ({
    type: SEARCH_SNOWFLAKE_OBJECTS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: `SELECT SYSTEM$NGRAM_SEARCH('${searchString}', 200)`
    },
    onSucceeded: ({ getState, dispatch, data }) => {
        const { schemas } = transformSnowflakeSearchResults(data.valuesByColumn[0][0]);
        const state = getState();
        const tables = Object.values(schemas).reduce((acc, { database, schema }) => {
            const sources = selectAllSourcesByDatabaseAndSchema(state, {
                database,
                schema
            });
            return acc.concat(sources);
        }, []);
        dispatch(doGetTableDetailsForDataBrowser({ tables }));
    }
});

export const doGetSnowflakeDatabases = (isDestinationTree) => ({
    type: GET_SNOWFLAKE_DATABASES,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: 'SHOW TERSE DATABASES IN ACCOUNT'
    },
    meta: { isDestinationTree }
});

export const doGetSnowflakeSchemasInDatabase = ({ database, isDestinationTree }) => ({
    type: GET_SNOWFLAKE_SCHEMAS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: `SHOW TERSE SCHEMAS IN DATABASE ${quoteIfNeeded(database)}`
    },
    meta: { database, isDestinationTree }
});

export const doGetSnowflakeObjectsInSchema = ({ schemaFqn, followUp }) => ({
    type: GET_SNOWFLAKE_SOURCES,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: `SHOW TERSE OBJECTS IN SCHEMA ${schemaFqn}`
    },
    meta: { schemaFqn },
    onSucceeded: ({ dispatch, getState }) => {
        if (followUp) {
            const { database, schema } = splitFqn(schemaFqn);
            const state = getState();
            const tables = selectAllSourcesByDatabaseAndSchema(state, { database, schema });

            dispatch(
                followUp({
                    database,
                    schema,
                    count: size(
                        selectFilteredSourcesByDatabaseAndSchema(state, { database, schema })
                    ),
                    tables
                })
            );
        }
    }
});

export const doGetSnowflakeObjectsAndTableDetailsInSchema = ({ schemaFqn }) =>
    doGetSnowflakeObjectsInSchema({ schemaFqn, followUp: doGetTableDetailsForDataBrowser });

export const doGetSnowflakeSourceColumns = (fqn) => ({
    type: GET_SNOWFLAKE_SOURCE_COLUMNS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: `SHOW COLUMNS IN TABLE ${fqn}`
    },
    meta: { fqn }
});

export const doLoadSnowflakeWarehouses = (role) => ({
    type: LIST_SNOWFLAKE_WAREHOUSES,
    api: { version: 2, name: 'ListSnowflakeWarehouses' },
    ...(role && { params: { role } })
});

export const doLoadUserSessionWarehouses = () => ({
    type: LIST_USER_SESSION_WAREHOUSES,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: 'SHOW WAREHOUSES'
    }
});

export const doSetUserSessionWarehouse = ({ warehouse, showNotification = false }) => ({
    type: SET_USER_SESSION_WAREHOUSE,
    api: { version: 2, name: 'SetSnowflakeWarehouse' },
    params: {
        warehouse
    },
    meta: { showNotification },
    onSucceeded: ({ dispatch }) => {
        dispatch(doLoadUserSessionWarehouses());
    }
});

export const doLoadSnowflakeRoles = () => ({
    type: LIST_SNOWFLAKE_ROLES,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: {
        sql: 'SELECT CURRENT_ROLE(), CURRENT_AVAILABLE_ROLES()'
    }
});

export const doSetSnowflakeRole = ({ role, showNotification = false }) => ({
    type: SET_SNOWFLAKE_ROLE,
    api: { version: 2, name: 'SetSnowflakeRole' },
    params: { role },
    meta: { showNotification },
    onSucceeded: ({ dispatch }) => {
        dispatch(doLoadSnowflakeRoles());
    }
});

export const doLoadDatameerWarehouse = () => ({
    type: GET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    api: GET_DATAMEER_SNOWFLAKE_WAREHOUSE_OPERATION
});

export const doSetSnowflakeWarehouse = (warehouse) => ({
    type: SET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    api: SET_DATAMEER_SNOWFLAKE_WAREHOUSE_OPERATION,
    params: { warehouse },
    onSucceeded: () => {
        if (isProject()) {
            navigateToHome();
        }
    }
});

export const doDeleteSnowflakeTable = ({ fqn, type }) => ({
    type: DELETE_SNOWFLAKE_TABLE,
    api: { version: 2, name: 'DeleteSnowflakeTable' },
    params: {
        database: fqn.database,
        schema: fqn.schema,
        table: fqn.table,
        tableType: type
    },
    onSucceeded: ({ dispatch }) => {
        dispatch(doDeleteFQNQuery({ fqn }));
        dispatch(doDeleteRowCountQuery({ fqnOrId: fqn }));
        dispatch(
            doGetSnowflakeObjectsInSchema({
                schemaFqn: toStringFqn({ database: fqn.database, schema: fqn.schema })
            })
        );
        dispatch(doValidateSnowflakeAccess([fqn]));
    }
});
